// Shared CSS animations used across screens — injected once at the App level
// (see App.tsx) so screen-local stylesheets can reference these keyframes by
// name without redefining them locally.
//
// Keyframes are ported verbatim from tailwind.config.js (theme.extend.keyframes
// / .animation) so the Tailwind→vanilla migration can drop the `animate-*`
// utilities. Reference a keyframe by name from any stylesheet, or use the helper
// classes below as a drop-in replacement for the legacy utilities:
//   animate-notification-slide-in   → .anim-notification-slide-in
//   animate-notification-slide-out  → .anim-notification-slide-out
//   animate-notification-progress   → .anim-notification-progress
//   animate-bounce-in               → .anim-bounce-in
//   animate-attention-pulse         → .anim-attention-pulse
//   animate-emoji-pop               → .anim-emoji-pop
//   animate-countdown-pulse         → .anim-countdown-pulse
//   animate-shake-x                 → .anim-shake-x
//   animate-blink                   → .anim-blink
//   animate-word-fade-in            → .anim-word-fade-in
//   animate-message-slide-in        → .anim-message-slide-in
//   animate-spin / -pulse / -ping   → .anim-spin / .anim-pulse / .anim-ping
//
// To add a new shared keyframe: append it here, then reference it from any
// screen's stylesheet via `animation: <name> ...;`.

export const sharedAnimations = `
  @keyframes notificationSlideIn {
    0%   { transform: translateX(100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
  }
  @keyframes notificationSlideOut {
    0%   { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(100%); opacity: 0; }
  }
  @keyframes notificationProgress {
    0%   { width: 100%; }
    100% { width: 0%; }
  }
  @keyframes bounceIn {
    0%   { transform: scale(0); opacity: 0; }
    50%  { transform: scale(1.15); }
    70%  { transform: scale(0.95); }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes attentionPulse {
    0%   { box-shadow: 0 0 0 0 rgba(172, 215, 71, 0.6); }
    50%  { box-shadow: 0 0 0 12px rgba(172, 215, 71, 0); }
    100% { box-shadow: 0 0 0 0 rgba(172, 215, 71, 0); }
  }
  @keyframes countdownPulse {
    0%   { transform: scale(0.4); opacity: 0; }
    30%  { transform: scale(1.3); opacity: 1; }
    50%  { transform: scale(1); opacity: 1; }
    80%  { transform: scale(1); opacity: 1; }
    100% { transform: scale(0.8); opacity: 0; }
  }
  @keyframes shakeX {
    0%, 100%      { transform: translateX(0); }
    15%, 45%, 75% { transform: translateX(-6px); }
    30%, 60%, 90% { transform: translateX(6px); }
  }
  @keyframes emojiPopIn {
    0%   { transform: scale(0) translateY(0); opacity: 0; }
    15%  { transform: scale(1.2) translateY(-2px); opacity: 1; }
    30%  { transform: scale(1) translateY(0); opacity: 1; }
    80%  { transform: scale(1) translateY(0); opacity: 1; }
    100% { transform: scale(0.8) translateY(-4px); opacity: 0; }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0; }
  }
  @keyframes wordFadeIn {
    0%   { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes messageSlideIn {
    0%   { opacity: 0; transform: translateY(8px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  /* Tailwind defaults (animate-spin / -pulse / -ping) used by loaders/spinners. */
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 50% { opacity: 0.5; } }
  @keyframes ping { 75%, 100% { transform: scale(2); opacity: 0; } }

  .anim-notification-slide-in { animation: notificationSlideIn 300ms ease-out forwards; }
  .anim-notification-slide-out { animation: notificationSlideOut 200ms ease-in forwards; }
  .anim-notification-progress { animation: notificationProgress 4000ms linear forwards; }
  .anim-bounce-in { animation: bounceIn 500ms ease-out forwards; }
  .anim-attention-pulse { animation: attentionPulse 1.5s ease-out 0.5s 3; }
  .anim-emoji-pop { animation: emojiPopIn 5s ease-out forwards; }
  .anim-countdown-pulse { animation: countdownPulse 1s ease-out forwards; }
  .anim-shake-x { animation: shakeX 0.6s ease-in-out; }
  .anim-blink { animation: blink 0.8s ease-in-out infinite; }
  .anim-word-fade-in { animation: wordFadeIn 800ms ease-out forwards; }
  .anim-message-slide-in { animation: messageSlideIn 1s ease-out both; }
  .anim-spin { animation: spin 1s linear infinite; }
  .anim-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
  .anim-ping { animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite; }
`;
